// 与管理端保持一致：必须使用同一 server2 地址，否则报名成功但管理端看不到数据
// 默认用空字符串 = 相对路径，请求发到当前页面同源，由 server3 代理到 server2（或 Vite 开发代理到 3030）
const BASE =
  import.meta.env.VITE_DRIP_PLAN_API_URL ||
  import.meta.env.VITE_DRIP_PLAN_BASE_URL ||
  "";

export type DripPackage = {
  id: number;
  name: string;
  price: number;
  imageUrl?: string;
  status: string;
  sort: number;
  description?: string;
  tag?: string;
  items?: { productId: number; quantity: number }[];
  productIds?: number[];
};

export async function fetchPublicPackages(): Promise<DripPackage[]> {
  const res = await fetch(`${BASE}/api/drip-plan/public/packages`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("获取套餐列表失败");
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

export type ApplySubmitPayload = {
  name: string;
  phone: string;
  workUnit: string;
  region: string;
  reason: string;
  proofCount: number;
  proofFileNames?: string[];
};

export async function uploadProofFile(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${BASE}/api/drip-plan/public/upload-proof`, {
    method: "POST",
    body: form,
  });
  const json = await res.json();
  if (json.code !== 0) throw new Error(json.message || "上传失败");
  const data = json.data as { filename?: string };
  if (!data?.filename) throw new Error("未返回文件名");
  return data.filename;
}

/** 按手机号查询申请列表（用于查询申请状态） */
export type ApplicationStatusItem = {
  id: number;
  applicationNumber: string;
  name: string;
  phone: string;
  workUnit: string;
  region: string;
  reason: string | null;
  proofCount: number;
  status: string;
  auditRemark: string | null;
  applyTime: string;
  updatedAt: string;
};

export async function queryApplicationsByPhone(phone: string): Promise<ApplicationStatusItem[]> {
  const res = await fetch(
    `${BASE}/api/drip-plan/public/query-by-phone?phone=${encodeURIComponent(phone.trim())}`,
    { method: "GET", headers: { Accept: "application/json" } }
  );
  const json = await res.json();
  if (json.code !== 0) throw new Error(json.message || "查询失败");
  const data = json.data as { list?: ApplicationStatusItem[] };
  return Array.isArray(data?.list) ? data.list : [];
}

export async function submitApply(payload: ApplySubmitPayload): Promise<{ applicationNumber: string }> {
  const res = await fetch(`${BASE}/api/drip-plan/public/apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const json = await res.json();
  if (json.code !== 0) throw new Error(json.message || "提交失败");
  const data = json.data as { applicationNumber?: string };
  if (!data?.applicationNumber) throw new Error("未返回申请编号");
  return { applicationNumber: data.applicationNumber };
}

/** 志愿者招募报名 */
export type VolunteerApplyPayload = {
  name: string;
  phone: string;
  position: string;
  availability: string;
  wechat?: string;
  skills?: string;
  intro?: string;
  resumeFilename?: string;
};

export async function uploadVolunteerResume(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${BASE}/api/drip-plan/public/volunteer-upload-resume`, {
    method: "POST",
    body: form,
  });
  const json = await res.json();
  if (json.code !== 0) throw new Error(json.message || "上传失败");
  const data = json.data as { filename?: string };
  if (!data?.filename) throw new Error("未返回文件名");
  return data.filename;
}

/** 带超时的 fetch，避免请求挂起导致按钮一直「提交中」 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeout?: number } = {}
): Promise<Response> {
  const { timeout = 20000, ...fetchOptions } = options;
  const ctrl = new AbortController();
  const id = setTimeout(() => ctrl.abort(), timeout);
  try {
    const res = await fetch(url, { ...fetchOptions, signal: ctrl.signal });
    clearTimeout(id);
    return res;
  } catch (e) {
    clearTimeout(id);
    if ((e as Error)?.name === "AbortError")
      throw new Error("网络超时，请检查网络后重试");
    throw e;
  }
}

export async function submitVolunteerApply(payload: VolunteerApplyPayload): Promise<void> {
  const res = await fetchWithTimeout(`${BASE}/api/drip-plan/public/volunteer-apply`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
    timeout: 20000,
  });
  const text = await res.text();
  let json: { code?: number; message?: string };
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    throw new Error(res.ok ? "提交失败" : `请求失败 (${res.status})，请稍后重试`);
  }
  if (json.code !== 0) throw new Error(json.message || "提交失败");
}

/** 首页一线故事/动态列表项 */
export type DynamicItem = {
  id: number;
  title: string;
  category: string;
  excerpt: string;
  image: string;
  views: number;
  date: string;
};

/** 故事详情（含正文） */
export type DynamicDetail = DynamicItem & {
  content: string;
  author?: string;
  tags?: string[];
};

/** 获取已发布的动态列表（首页一线故事） */
export async function fetchPublicDynamics(): Promise<DynamicItem[]> {
  const url = `${BASE}/api/drip-plan/public/dynamics?_t=${Date.now()}`;
  const res = await fetch(url, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

/** 获取单条动态详情（故事详情页） */
export async function fetchDynamicById(id: number): Promise<DynamicDetail | null> {
  const res = await fetch(`${BASE}/api/drip-plan/public/dynamics/${id}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  const json = await res.json();
  if (json.code !== 0 || !json.data) return null;
  return json.data as DynamicDetail;
}

/** 增加阅读量（故事详情页进入时可选调用） */
export async function incrementDynamicView(id: number): Promise<void> {
  await fetch(`${BASE}/api/drip-plan/public/dynamics/${id}/view`, { method: "POST" });
}

/** 温暖故事卡片项（项目页-温暖故事 tab） */
export type WarmStoryItem = {
  id: number;
  name: string;
  role: string;
  story: string;
  date: string;
  avatar: string;
  likeCount: number;
};

/** 获取温暖故事列表（项目页-受助者/捐赠者/志愿者） */
export async function fetchWarmStories(tab: "recipients" | "donors" | "volunteers"): Promise<WarmStoryItem[]> {
  const res = await fetch(
    `${BASE}/api/drip-plan/public/warm-stories?tab=${tab}&_t=${Date.now()}`,
    { method: "GET", headers: { Accept: "application/json" }, cache: "no-store" }
  );
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

/** 获取点滴动态列表（首页「点滴动态」板块，与一线故事同结构） */
export async function fetchDropsDynamics(): Promise<DynamicItem[]> {
  const res = await fetch(
    `${BASE}/api/drip-plan/public/drops-dynamics?_t=${Date.now()}`,
    { method: "GET", headers: { Accept: "application/json" }, cache: "no-store" }
  );
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

// ---------- 媒体报道（关于我们-媒体报道） ----------

export type MediaReportItem = {
  id: number;
  sourceName: string;
  sourceLogo: string;
  title: string;
  summary: string;
  publicationDate: string;
  articleLink: string;
};

export type MediaReportDetail = MediaReportItem & {
  content: string;
};

/** 获取已发布的媒体报道列表 */
export async function fetchPublicMediaReports(): Promise<MediaReportItem[]> {
  const res = await fetch(`${BASE}/api/drip-plan/public/media-reports`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

/** 获取单条媒体报道详情 */
export async function fetchMediaReportById(id: number): Promise<MediaReportDetail | null> {
  const res = await fetch(`${BASE}/api/drip-plan/public/media-reports/${id}`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) return null;
  const json = await res.json();
  if (json.code !== 0 || !json.data) return null;
  return json.data as MediaReportDetail;
}

// ---------- 认捐与资金公示 ----------

export type FundOverviewData = {
  totalIncome: number;
  totalExpense: number;
  balance: number;
  managementFeeRate: number;
  expenseCategories: { name: string; value: number; percentage: number }[];
  monthlyTrend: { month: string; value: number }[];
  donationCount: number;
  disbursementCount: number;
  totalBottlesDisbursed?: number;
  regionCount?: number;
  donationSourceBreakdown?: { name: string; value: number; percentage: number }[];
  monthlyBottlesTrend?: { month: string; bottles: number }[];
};

export async function fetchPublicFundOverview(): Promise<FundOverviewData | null> {
  const res = await fetch(`${BASE}/api/drip-plan/public/fund-overview?_t=${Date.now()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return null;
  const json = await res.json();
  if (json.code !== 0 || !json.data) return null;
  return json.data as FundOverviewData;
}

export type PublicDonorItem = {
  id: number;
  name: string;
  amount: number;
  isAnonymous: boolean;
  date: string;
};

export async function fetchPublicDonors(limit = 50): Promise<PublicDonorItem[]> {
  const res = await fetch(`${BASE}/api/drip-plan/public/donors?limit=${limit}&_t=${Date.now()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

export type DisbursementRecord = {
  id: number;
  title: string;
  category: string;
  amount: number;
  beneficiary: string;
  region: string;
  quantity: number | null;
  date: string;
  remark: string;
};

export async function fetchPublicDisbursements(params?: {
  region?: string;
  startDate?: string;
  endDate?: string;
}): Promise<DisbursementRecord[]> {
  const qs = new URLSearchParams();
  if (params?.region) qs.set("region", params.region);
  if (params?.startDate) qs.set("startDate", params.startDate);
  if (params?.endDate) qs.set("endDate", params.endDate);
  const q = qs.toString();
  const res = await fetch(`${BASE}/api/drip-plan/public/disbursements${q ? "?" + q + "&" : "?"}_t=${Date.now()}`, {
    method: "GET",
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!res.ok) return [];
  const json = await res.json();
  if (json.code !== 0 || !Array.isArray(json.data)) return [];
  return json.data;
}

export type DonateSubmitPayload = {
  donorName?: string;
  phone?: string;
  packageId?: number;
  packageName?: string;
  customAmount?: number;
  quantity?: number;
  payMethod?: "wechat" | "alipay";
  isAnonymous?: boolean;
  remark?: string;
};

export type DonateSubmitResult = {
  id: number;
  donationNo: string;
  donorName: string;
  amount: number;
  packageName: string;
  paidAt: string;
};

export async function submitDonation(payload: DonateSubmitPayload): Promise<DonateSubmitResult> {
  const res = await fetchWithTimeout(`${BASE}/api/drip-plan/public/donate`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      donorName: payload.donorName || "爱心人士",
      phone: payload.phone,
      packageId: payload.packageId,
      packageName: payload.packageName,
      customAmount: payload.customAmount,
      quantity: payload.quantity || 1,
      payMethod: payload.payMethod === "alipay" ? "支付宝" : "微信支付",
      isAnonymous: payload.isAnonymous,
      remark: payload.remark,
    }),
    timeout: 20000,
  });
  const json = await res.json();
  if (json.code !== 0) throw new Error(json.message || "捐赠提交失败");
  return json.data as DonateSubmitResult;
}
